import {
  InAppContentDefinition,
  MESSAGE_FORMAT,
  Position as PositionEnum,
} from 'modules/graphql/types';
import { ComponentType, useEffect } from 'react';
import styled from 'styled-components';
import { Card } from 'ui/card';
import { FormField } from 'ui/formField';
import {
  DesktopBottom,
  DesktopCenter,
  DesktopFull,
  DesktopTop,
  PhoneBottom,
  PhoneCenter,
  PhoneFull,
  PhoneTop,
} from 'ui/icons';
import { colors, spacing } from 'ui/theme';
import { prefixWithUserProject } from 'utils/prefixWithUserProject';
import { getIsEditMode } from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useLocalStorageOrUseState } from '../../hooks/useLocalStorageOrUseState';
import { IN_APP_DISPLAY, SETTINGS_DISPLAY_CONTENT } from '../constants';
import { getPositionFromInAppDisplaySetting } from '../utils';

const CardContainer = styled.div`
  gap: ${spacing.half};
  display: flex;
  flex-wrap: wrap;
`;

const CardLabel = styled.div`
  margin-left: ${spacing.quarter};
`;

const StyledCard = styled(Card)`
  align-items: center;
  display: flex;
  width: 45%;
`;

const StyledCardWithBorder = styled(StyledCard)<{ isSelected: boolean }>`
  background-color: ${({ isSelected }) => `${isSelected ? colors.purple10 : colors.white}`};
  border: ${({ isSelected }) =>
    `${isSelected ? colors.purple100 : colors.grey15} ${spacing.single} solid;`};
  border-radius: ${spacing.half};
  color: ${({ isSelected }) => (isSelected ? colors.purple100 : colors.textSecondary)};
  cursor: pointer;
  padding: ${spacing.half};
`;

type PositionOption = {
  DesktopIcon: ComponentType<React.PropsWithChildren<IconComponentProps>>;
  label: string;
  PhoneIcon: ComponentType<React.PropsWithChildren<IconComponentProps>>;
};

type IconComponentProps = {
  color: string;
};

/** Function to allow for conditionally applying color to the icons more easily in the getCardContent function */
function createIconComponent(Icon: ComponentType<React.PropsWithChildren<IconComponentProps>>) {
  return function IconComponent({ color }: IconComponentProps) {
    return <Icon color={color} />;
  };
}

const POSITION_OPTIONS: Record<PositionEnum, PositionOption> = {
  [PositionEnum.Top]: {
    label: 'Top',
    DesktopIcon: createIconComponent(DesktopTop),
    PhoneIcon: createIconComponent(PhoneTop),
  },
  [PositionEnum.Center]: {
    label: 'Center',
    DesktopIcon: createIconComponent(DesktopCenter),
    PhoneIcon: createIconComponent(PhoneCenter),
  },
  [PositionEnum.Bottom]: {
    label: 'Bottom',
    DesktopIcon: createIconComponent(DesktopBottom),
    PhoneIcon: createIconComponent(PhoneBottom),
  },
  [PositionEnum.Full]: {
    label: 'Full',
    DesktopIcon: createIconComponent(DesktopFull),
    PhoneIcon: createIconComponent(PhoneFull),
  },
};

/** Generates contents of card for position option */
const getCardContent = (positionOption: PositionOption, color: string) => {
  const { label, DesktopIcon, PhoneIcon } = positionOption;
  return (
    <>
      <DesktopIcon color={color} />
      <PhoneIcon color={color} />
      <CardLabel>{label}</CardLabel>
    </>
  );
};

export const Position = () => {
  const { view } = useQueryParamContext();
  const { initialTemplate, isEditMode, liveTemplate, setCurrentMediumField } =
    useEditableFieldsContext();

  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const inAppMediumData = (liveTemplate?.mediumData || {}) as InAppContentDefinition;
  const { inAppDisplaySettings, messageFormat } = inAppMediumData;
  const liveTemplatePosition = getPositionFromInAppDisplaySetting(inAppDisplaySettings);
  const isNewTemplate = !initialTemplate.name?.length;
  const [positionTileSelection, setPositionTileSelection] = useLocalStorageOrUseState<PositionEnum>(
    prefixWithUserProject('web-inapp-display-type'),
    PositionEnum.Center,
    liveTemplatePosition,
    isNewTemplate,
  );

  const handlePositionSelection = (newPosition: PositionEnum) => {
    setPositionTileSelection(newPosition);
    const newInAppDisplaySettings = {
      ...inAppDisplaySettings,
      ...IN_APP_DISPLAY[newPosition],
    };
    setCurrentMediumField('inAppDisplaySettings', newInAppDisplaySettings);
  };

  useEffect(() => {
    setPositionTileSelection(liveTemplatePosition);
  }, [isEditModeVisible]);

  return (
    <>
      {messageFormat !== MESSAGE_FORMAT.Json && (
        <FormField
          data-test="settings-position-field"
          isHorizontalLayout
          labelText={SETTINGS_DISPLAY_CONTENT.position.labelText}
        >
          {isEditModeVisible ? (
            <CardContainer>
              {(Object.keys(POSITION_OPTIONS) as PositionEnum[]).map(
                (positionOptionKey: PositionEnum) => {
                  const isSelected = positionOptionKey === positionTileSelection;
                  return (
                    <StyledCardWithBorder
                      data-test={`position-option-${positionOptionKey.toLowerCase()}${
                        isSelected ? '-selected' : ''
                      }`}
                      key={POSITION_OPTIONS[positionOptionKey].label}
                      isSelected={isSelected}
                      onClick={() => handlePositionSelection(positionOptionKey)}
                    >
                      {getCardContent(
                        POSITION_OPTIONS[positionOptionKey],
                        isSelected ? colors.purple50 : colors.grey15,
                      )}
                    </StyledCardWithBorder>
                  );
                },
              )}
            </CardContainer>
          ) : (
            <StyledCard>
              {inAppDisplaySettings &&
                getCardContent(POSITION_OPTIONS[liveTemplatePosition], colors.grey15)}
            </StyledCard>
          )}
        </FormField>
      )}
    </>
  );
};
